import { css } from '@emotion/css'
import { noop } from 'lodash'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { mapReleaseDate } from '../../../../graphql/schema/queries/helpers/library'
import { LibraryAgentLanguageValue } from '../library/library-agent-language'
import { Severity, VulnerabilitySeverityBadge } from '../severity'
import { LatestVersionCell } from '../cell-content/latest-version-cell'
import { NameCell } from '../cell-content/name-cell'
import {
  VulnerabilitiesCell,
  VulnerabilitiesCellVulnerability
} from '../cell-content/vulnerabilities-cell'
import { CvssScoringType } from '../../helpers/ui-interfaces'
import { ProjectsCell } from '../cell-content/projects-cell'
import { LibrarySCA } from '../../helpers/libraries-interfaces'
import { getGoLibraryName, isPrivateLibrary } from '../../helpers/helpers'
import { Organization } from '@contrast-security-inc/base-types'

const projectsCellCss = css`
  max-width: 100%;
  margin: 6px 6px 6px 0px;
`

interface Props {
  organization: Organization
  setPanelLibrary: (library: LibrarySCA) => void
}

export const getColumns = ({ organization, setPanelLibrary }: Props) => {
  return [
    {
      accessorKey: 'SEVERITY',
      id: 'severity',
      size: 50,
      enableSorting: true,
      header: () => <FormattedMessage id="title_severityLevel" />,
      cell: ({ row: { original: library } }) => {
        const isPrivate = isPrivateLibrary(library)

        return (
          <div
            className={css`
              width: 120px;
              min-width: 120px;
              max-width: 120px;
            `}
          >
            <div
              className={css`
                width: 24px;
              `}
            >
              <VulnerabilitySeverityBadge
                severity={Severity.fromName(
                  isPrivate
                    ? Severity.UNKNOWN.name
                    : library.severity || Severity.CLEAN.name
                )}
              />
            </div>
          </div>
        )
      }
    },
    {
      accessorKey: 'FILE_NAME',
      enableSorting: false,
      id: 'file_name',
      size: 500,
      header: () => <FormattedMessage id="title_library" />,
      cell: ({ row: { original: library } }) => (
        <div
          className={css`
            width: 450px;
            min-width: 250px;
            margin-left: -10px;
            flex: 1;
          `}
        >
          {(() => {
            const name =
              library.libraryLanguage.toUpperCase() === 'GO'
                ? getGoLibraryName(library)
                : library.artifactName

            return (
              <>
                {library && (
                  <NameCell
                    organizationID={organization.organizationUuid}
                    appLibraryStatusEnabled={false}
                    rowActiveInPanel={false}
                    applicationId={'applicationId'}
                    setSelectedPanelTabId={noop}
                    onClick={() => {
                      setPanelLibrary(library)
                    }}
                    rowData={{
                      name,
                      hash: library.hash,
                      language: library.libraryLanguage,
                      ossEnabled: false,
                      tags: [],
                      bugTrackers: [],
                      versions: {
                        latest: {
                          version: library.latestVersion,
                          releaseDate: mapReleaseDate(
                            library.latestVersionReleaseDate
                          )
                        },
                        current: {
                          version: library.version,
                          releaseDate: mapReleaseDate(library.releaseDate)
                        }
                      },
                      policies: {
                        invalidVersion: false,
                        restrictedByOrg: false,
                        restrictedLicense: false,
                        versionRestriction: 0
                      },
                      licenses: [],
                      usage: { totalUsed: 10 },
                      id: library.hash
                    }}
                  />
                )}
              </>
            )
          })()}
        </div>
      )
    },
    {
      accessorKey: 'LATEST_VERSION',
      enableSorting: false,
      id: 'LatestVersion',
      size: 100,
      cell: ({ row: { original: library } }) => (
        <>
          {library && (
            <div
              className={css`
                width: 160px;
                min-width: 100px;
              `}
            >
              <LatestVersionCell
                releaseDate={mapReleaseDate(library.latestVersionReleaseDate)}
                version={library.latestVersion}
                compareVersion={library.version}
                language={library.libraryLanguage as LibraryAgentLanguageValue}
              />
            </div>
          )}
        </>
      ),
      header: () => (
        <div
          className={css`
            width: 160px;
            min-width: 100px;
          `}
        >
          <FormattedMessage id="title_libraryStatistics_latestVersion" />
        </div>
      )
    },
    {
      enableSorting: false,
      accessorKey: 'VULNERABILITIES',
      id: 'Vulnerabilities',
      size: 300,
      cell: ({ row: { original: library } }) => {
        const isPrivate = isPrivateLibrary(library)

        return (
          <div
            className={css`
              min-width: 350px;
            `}
          >
            {(() => {
              const vulns = !library.vulnerabilities
                ? []
                : library.vulnerabilities.map(
                    (lib): VulnerabilitiesCellVulnerability => {
                      return {
                        ...lib,
                        visible: true,
                        cvss3SeverityCode: lib.cvss3Severity,
                        references: [],
                        severityCode: lib.severity,
                        hasCve: true
                      }
                    }
                  )

              return (
                <VulnerabilitiesCell
                  rowData={{
                    name: library.artifactName,
                    vulnerabilities: vulns
                  }}
                  cvssScoringType={
                    organization.cvssScoringType as CvssScoringType
                  }
                  onClick={() => {
                    setPanelLibrary(library)
                  }}
                  isPrivate={isPrivate}
                />
              )
            })()}
          </div>
        )
      },
      header: () => <FormattedMessage id="title_vulnerabilitiesCves" />
    },
    {
      accessorKey: 'PROJECTS',
      enableSorting: false,
      id: 'Projects',
      size: 100,
      header: () => <FormattedMessage id="title_projects" />,
      cell: ({ row: { original: library } }) => {
        return (
          <div className={projectsCellCss}>
            {(() => {
              return (
                <ProjectsCell
                  projects={library.projects}
                  library={library.fileName}
                />
              )
            })()}
          </div>
        )
      }
    }
  ]
}
